/**
 * 일일 로컬 자정 리셋 (Phaser/도메인 비의존, 순수 로직).
 *
 * 던전 도전권·일일 보상·일일 미션 등 "매일 로컬 자정(또는 지정 시각)에 한 번 리셋"되는 모든 카운터에 사용.
 * 리셋 시각 판정은 이 패키지가, **마지막 리셋 시각의 영속 저장은 프로젝트가 `ResetStore` Port로 위임받는다**(DIP).
 * 시계(`Clock`)를 주입받아 테스트 결정론을 확보한다 — `@yjsgames/weighted-roll`의 RandomSource 주입과 동형.
 *
 * 레퍼런스: idleblade `unity-timer`/ResetSchedule (Daily/DeviceLocal). 여기선 로컬 자정 MVP로 축소.
 */

/** 현재 시각(epoch ms)을 반환하는 시계 소스. 주입으로 테스트 결정론 확보 (기본 `Date.now`). */
export type Clock = () => number;

/**
 * 마지막 리셋 시각(epoch ms) 영속 Port — DIP. 패키지는 "언제 리셋인가"만 판정하고,
 * 실제 timestamp 읽기/쓰기는 프로젝트가 SaveData 등 저장소에 구현한다.
 * (idleblade `ResetTimerSaveEntry`가 담당하던 역할의 TS 대응.)
 */
export interface ResetStore {
  /** key의 마지막 리셋 시각(epoch ms). 한 번도 리셋 안 했으면 `undefined`. */
  getLastReset(key: string): number | undefined;
  /** key의 마지막 리셋 시각을 ms로 기록. */
  setLastReset(key: string, ms: number): void;
}

/** 일일 리셋 스케줄 — 매일 로컬 `resetHour`시 기준. */
export interface ResetSchedule {
  /** 리셋 식별 키 (`ResetStore`의 저장 키). 예: "dungeon-tickets". */
  readonly key: string;
  /** 리셋 기준 시각(로컬 0~23시). 기본 0(자정). 예: 4 = 매일 새벽 4시 리셋. */
  readonly resetHour?: number;
}

/** 리셋 발생 결과. */
export interface ResetEvent {
  readonly key: string;
  /** 마지막 리셋 이후 통과한 리셋 경계 수 (1=정상, 2+=며칠 미접속). */
  readonly missedCount: number;
  /** 이번 리셋 경계 시각(epoch ms). */
  readonly resetAtMs: number;
}

const MAX_MISSED_ITERATIONS = 100_000; // 무한 루프 가드 (시간 조작/손상값 방어)

function normalizeHour(resetHour: number | undefined): number {
  const h = Math.floor(resetHour ?? 0);
  return Math.min(23, Math.max(0, h));
}

/**
 * `nowMs`가 속한 로컬 '리셋일'의 경계(가장 최근 리셋 시각 ≤ now) epoch ms.
 * 로컬 시간대 기준 — `Date` 기본 동작이 로컬이라 timezone 자동 처리.
 * `resetHour`(기본 0=자정)가 오늘 아직 안 지났으면 어제 경계를 반환한다.
 */
export function localResetBoundary(nowMs: number, resetHour?: number): number {
  const d = new Date(nowMs);
  d.setHours(normalizeHour(resetHour), 0, 0, 0);
  // 오늘 리셋 시각 전이면 어제 경계. 캘린더 1일 후퇴(DST 안전 — 고정 ms 감산은 DST일 1시간 오차).
  if (d.getTime() > nowMs) d.setDate(d.getDate() - 1);
  return d.getTime();
}

/** 다음 리셋 경계까지 남은 ms (리셋 카운트다운 표시용). 항상 양수(0~24h). */
export function msUntilNextReset(nowMs: number, resetHour?: number): number {
  const next = nextResetBoundary(nowMs, resetHour);
  return Math.max(0, next - nowMs);
}

/** `nowMs` 이후 첫 리셋 경계(다음 자정/resetHour) epoch ms. DST 안전 — 캘린더 1일 진행. */
function nextResetBoundary(nowMs: number, resetHour?: number): number {
  const d = new Date(localResetBoundary(nowMs, resetHour));
  d.setDate(d.getDate() + 1);
  return d.getTime();
}

/**
 * `storedBoundary`(마지막 리셋 경계) → `currentBoundary`(현재 경계)까지 통과한 리셋 경계 수.
 * 캘린더 1일씩 진행하므로 DST(23/25시간 day)에서도 정확하다. idleblade `AdvanceAndCount` 이식.
 */
function countMissedResets(storedBoundaryMs: number, currentBoundaryMs: number): number {
  if (storedBoundaryMs >= currentBoundaryMs) return 0;
  let count = 0;
  const cursor = new Date(storedBoundaryMs);
  while (cursor.getTime() < currentBoundaryMs && count < MAX_MISSED_ITERATIONS) {
    cursor.setDate(cursor.getDate() + 1);
    count += 1;
  }
  return count;
}

/**
 * 키별 일일 리셋 스케줄을 관리한다. `check()` 호출 시 각 스케줄의 리셋 경계 통과 여부를
 * `ResetStore`(프로젝트 위임)로 판정해, 통과한 키에 대해 store를 갱신하고 리스너를 발화한다.
 * 자체 상태는 스케줄·리스너뿐 — 마지막 리셋 시각의 SSoT는 `ResetStore`(프로젝트 SaveData).
 */
export class DailyResetService {
  private readonly schedules = new Map<string, ResetSchedule>();
  private readonly listeners = new Set<(event: ResetEvent) => void>();

  constructor(
    private readonly clock: Clock,
    private readonly store: ResetStore,
  ) {}

  /**
   * 스케줄 등록. 최초 등록(store에 lastReset 부재) 시 현재 경계로 시드해
   * 부팅 직후 유령 리셋을 방지한다. 이미 저장된 키는 그대로 둔다.
   */
  register(schedule: ResetSchedule): void {
    this.schedules.set(schedule.key, schedule);
    if (this.store.getLastReset(schedule.key) === undefined) {
      this.store.setLastReset(schedule.key, localResetBoundary(this.clock(), schedule.resetHour));
    }
  }

  /** 등록 해제. */
  unregister(key: string): void {
    this.schedules.delete(key);
  }

  /** 리셋 발생 리스너 구독. 반환값 호출로 구독 해제. */
  onReset(listener: (event: ResetEvent) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * 등록된 모든 스케줄의 리셋 경계 통과 여부를 검사한다.
   * 통과한 키는 store를 현재 경계로 갱신 + 리스너 발화. 발생한 이벤트 배열을 반환(동기 사용용).
   * 같은 리셋일 내 재호출은 멱등(빈 배열).
   */
  check(): ResetEvent[] {
    const now = this.clock();
    const events: ResetEvent[] = [];
    for (const schedule of this.schedules.values()) {
      const boundary = localResetBoundary(now, schedule.resetHour);
      const last = this.store.getLastReset(schedule.key) ?? boundary;
      if (last >= boundary) continue;
      const event: ResetEvent = {
        key: schedule.key,
        missedCount: countMissedResets(last, boundary),
        resetAtMs: boundary,
      };
      this.store.setLastReset(schedule.key, boundary);
      events.push(event);
      for (const listener of this.listeners) listener(event);
    }
    return events;
  }

  /** 다음 리셋까지 남은 ms (UI 카운트다운). 미등록 키는 0. */
  msUntilNextReset(key: string): number {
    const schedule = this.schedules.get(key);
    if (!schedule) return 0;
    return msUntilNextReset(this.clock(), schedule.resetHour);
  }

  /**
   * 치트/QA 전용 — 등록된 모든 스케줄의 lastReset을 0으로 만들어
   * 다음 `check()`에서 강제 리셋되게 한다 (자정 대기 없이 리셋 사이클 검증).
   */
  forceResetAll(): void {
    for (const key of this.schedules.keys()) this.store.setLastReset(key, 0);
  }
}
